// src/services/api.js

import axios from 'axios';
import { toast } from 'react-toastify';

// Definir la URL base de la API
// En local usa .env.development, en producción usa .env.production (se selecciona al hacer npm run build)
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Crear una instancia de Axios con la URL base
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token JWT a las solicitudes
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    // Buscar token en localStorage, si no está, buscar en sessionStorage
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No se encontró token JWT para la solicitud', config.url);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
  
// Interceptor para manejar errores y notificaciones
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.message || 'Ocurrió un error en la solicitud';
    toast.error(`Error: ${errorMessage}`);
    return Promise.reject(error);
  }
);

// Función de utilidad para ordenar por nombre
const sortByName = (data) => data.sort((a, b) => a.nombre.localeCompare(b.nombre));

// Función para formatear datos de registro antes de enviar
const formatRegistroData = (data) => ({
  ...data,
  usuario_id: Number(data.usuario_id),
  actividad_id: Number(data.actividad_id),
  seccion_id: Number(data.seccion_id),
  fecha: data.fecha, // No convertir a ISO, enviar como está
});

// ===================
// FUNCIONES DE LOGIN
// ===================

export const login = async (credentials) => {
  try {
    const response = await api.post('/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error en login:', error);
    throw error;
  }
};

// ==============================
// FUNCIONES DE USUARIOS
// ==============================

// Función para obtener centros de producción desde tabla real
export const getCentrosProduccion = async () => {
  try {
    const response = await api.get('/centros-produccion');
    return response.data;
  } catch (error) {
    console.error('Error al obtener centros de producción:', error);
    throw error;
  }
};

export const getUsuarios = async () => {
  try {
    const response = await api.get('/usuarios');
    return response.data;
  } catch (error) {
    console.error('Error en getUsuarios:', error);
    throw error;
  }
};

export const createUsuario = async (usuario) => {
  try {
    const response = await api.post('/usuarios', usuario);
    return response.data;
  } catch (error) {
    console.error('Error al crear el usuario:', error);
    throw error;
  }
};

export const getPrecipitaciones = async (fechaInicio = null, fechaFin = null, centro_produccion_id = null) => {
  try {
    const params = {};
    if (fechaInicio) params.fechaInicio = fechaInicio;
    if (fechaFin) params.fechaFin = fechaFin;
    if (centro_produccion_id) params.centro_produccion_id = centro_produccion_id;
    const response = await api.get('/precipitacion', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener precipitaciones:', error);
    throw error;
  }
};

export const createPrecipitacion = async (data) => {
  try {
    const response = await api.post('/precipitacion', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear precipitación:', error);
    throw error;
  }
};

export const deleteUsuario = async (id) => {
  try {
    const response = await api.delete(`/usuarios/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el usuario:', error);
    throw error;
  }
};

export const updateUsuario = async (id, usuario) => {
  try {
    const response = await api.put(`/usuarios/${id}`, usuario);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el usuario:', error);
    throw error;
  }
};

// ==============================
// FUNCIONES DE REGISTROS
// ==============================

export const getRegistros = async (fechaInicio = null, fechaFin = null) => {
  try {
    const params = {};
    if (fechaInicio) params.fechaInicio = fechaInicio;
    if (fechaFin) params.fechaFin = fechaFin;
    const response = await api.get('/registros-operarios', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener registros:', error);
    throw error;
  }
};

export const createRegistroOperario = async (data) => {
  try {
    const response = await api.post('/registros-operarios', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear registro operario:', error);
    throw error;
  }
};
  
export const updateRegistro = async (id, data) => {
  try {
    const formattedData = formatRegistroData(data);
    const response = await api.put(`/registros-operarios/${id}`, formattedData);
    return response.data;
  } catch (error) {
    console.error("Error al actualizar el registro en la API:", error.response?.data || error.message || 'Error desconocido');
    throw error;
  }
};

export const deleteRegistro = async (id) => {
  try {
    const response = await api.delete(`/registros-operarios/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar el registro:', error);
    throw error;
  }
};

// ==============================
// FUNCIONES DE ACTIVIDADES
// ==============================

export const getActividades = async () => {
  try {
    const response = await api.get('/actividades');
    return sortByName(response.data);
  } catch (error) {
    console.error('Error en getActividades:', error);
    return [];
  }
};

export const getSubactividades = async (actividadId) => {
  try {
    const response = await api.get(`/subactividades/actividad/${actividadId}`);
    return response.data;
  } catch (error) {
    console.error("Error al obtener subactividades:", error);
    return [];
  }
};

// ==============================
// FUNCIONES DE SECCIONES Y LOTES
// ==============================

export const getSecciones = async () => {
  try {
    const response = await api.get('/secciones');
    return sortByName(response.data);
  } catch (error) {
    console.error('Error en getSecciones:', error);
    return [];
  }
};

export const getLotes = async (seccionId) => {
  try {
    const response = await api.get(`/lotes/seccion/${seccionId}`);
    return sortByName(response.data);
  } catch (error) {
    console.error('Error en getLotes:', error);
    return [];
  }
};


// ==============================
// FUNCIONES ADICIONALES
// ==============================

export const getRegistrosPorOperarioFecha = async (operario_id, fecha) => {
  try {
    const response = await api.get('/registros', { params: { operario_id, fecha } });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo registros por operario y fecha:', error);
    return [];
  }
};

export const calcularHorasLabor = async (data) => {
  try {
    if (!data.horaInicio || !data.horaFin) {
      throw new Error("Los campos 'horaInicio' y 'horaFin' son obligatorios.");
    }
    if (isNaN(data.actividad_id)) {
      throw new Error("El 'actividad_id' debe ser un número válido.");
    }

    const formattedData = {
      hora_inicio: data.horaInicio,
      hora_fin: data.horaFin,
      horasLluvia: data.horasLluvia || "0",
      actividad_id: data.actividad_id,
      descuento_desayuno: data.descuento_desayuno || false,
    };

    const response = await api.post("/calcular-horas-labor", formattedData);
    return response.data.horas_labor;
  } catch (error) {
    console.error("Error al calcular las horas laborables:", error.response?.data || error.message);
    throw error;
  }
};

// ==============================
// FUNCIONES DE PLAN DE TRABAJO
// ==============================

export const getPlanesTrabajo = async (filtros = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    const response = await api.get(`/planes-trabajo?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener planes de trabajo:', error);
    throw error;
  }
};

export const getPlanTrabajoById = async (id) => {
  try {
    const response = await api.get(`/planes-trabajo/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener plan de trabajo:', error);
    throw error;
  }
};

export const createPlanTrabajo = async (planData) => {
  try {
    const response = await api.post('/planes-trabajo', planData);
    return response.data;
  } catch (error) {
    console.error('Error al crear plan de trabajo:', error);
    throw error;
  }
};

export const updatePlanTrabajo = async (id, planData) => {
  try {
    const response = await api.put(`/planes-trabajo/${id}`, planData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar plan de trabajo:', error);
    throw error;
  }
};

export const deletePlanTrabajo = async (id) => {
  try {
    const response = await api.delete(`/planes-trabajo/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar plan de trabajo:', error);
    throw error;
  }
};

export const getEstadisticasPlanTrabajo = async (filtros = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    const response = await api.get(`/planes-trabajo/estadisticas?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de plan de trabajo:', error);
    throw error;
  }
};

export const updateActividadEstado = async (planId, actividadId, estadoData) => {
  try {
    const response = await api.patch(`/planes-trabajo/${planId}/actividades/${actividadId}/estado`, estadoData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar estado de actividad:', error);
    throw error;
  }
};

// ==============================
// FUNCIONES DE PLAN MENSUAL
// ==============================

export const getPlanesMensuales = async (filtros = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    const response = await api.get(`/planes-mensuales?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener planes mensuales:', error);
    throw error;
  }
};

export const getPlanMensualById = async (id) => {
  try {
    const response = await api.get(`/planes-mensuales/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener plan mensual:', error);
    throw error;
  }
};

export const createPlanMensual = async (planData) => {
  try {
    const response = await api.post('/planes-mensuales', planData);
    return response.data;
  } catch (error) {
    console.error('Error al crear plan mensual:', error);
    throw error;
  }
};

export const createActividadDiaria = async (planId, actividadData) => {
  try {
    const response = await api.post(`/planes-mensuales/${planId}/actividades`, actividadData);
    return response.data;
  } catch (error) {
    console.error('Error al crear actividad diaria:', error);
    throw error;
  }
};

export const updateActividadDiaria = async (planId, actividadId, dia, actividadData) => {
  try {
    const response = await api.patch(`/planes-mensuales/${planId}/actividades/${actividadId}/dia/${dia}`, actividadData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar actividad diaria:', error);
    throw error;
  }
};

export const deleteActividadDiaria = async (planId, actividadId, dia) => {
  try {
    const url = `/planes-mensuales/${planId}/actividades/${actividadId}/dia/${dia}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar actividad diaria:', error);
    throw error;
  }
};

export const getVistaCalendario = async (planId) => {
  try {
    const response = await api.get(`/planes-mensuales/${planId}/calendario`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener vista de calendario:', error);
    throw error;
  }
};

export const cerrarPlanMensual = async (planId, observacionesCierre) => {
  try {
    const response = await api.patch(`/planes-mensuales/${planId}/cerrar`, {
      observaciones_cierre: observacionesCierre
    });
    return response.data;
  } catch (error) {
    console.error('Error al cerrar plan mensual:', error);
    throw error;
  }
};

export const deletePlanMensual = async (id) => {
  try {
    const response = await api.delete(`/planes-mensuales/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar plan mensual:', error);
    throw error;
  }
};

export const getEstadisticasPlanMensual = async (filtros = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key]) {
        params.append(key, filtros[key]);
      }
    });
    
    const response = await api.get(`/planes-mensuales/estadisticas?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de plan mensual:', error);
    throw error;
  }
};
